// Claves SSH en el navegador (tipo de item `ssh`). Plan: design/SETTINGS-DEVELOPER-PRIVACY.md 5.2, "Estado de la fase 6".
//
// Aqui solo se GENERA y se EXPORTA (WebCrypto, la clave privada no sale del navegador hasta que se guarda el item):
//   - Ed25519 si el navegador lo soporta; si no, ECDSA P-256. RSA 3072/4096 y ECDSA P-384 tambien.
//   - La privada sale en formato OpenSSH (-----BEGIN OPENSSH PRIVATE KEY-----), sin cifrar o, con passphrase,
//     cifrada como ssh-keygen: bcrypt_pbkdf (16 rondas) + aes256-ctr.
//   - La publica sale en formato authorized_keys y la huella SHA256 en base64 sin relleno, como `ssh-keygen -lf`.
// Importar (detectar el formato, derivar la publica, comprobar la passphrase) lo hace el servidor: ver inspectKey().
/* global globalThis */
import { bcryptPbkdf } from './bcryptPbkdf';
import api from '../services/api';

export const KEY_TYPES = ['ed25519', 'ecdsa-p256', 'ecdsa-p384', 'rsa-3072', 'rsa-4096'];
const RSA_SIZES = { 'rsa-3072': 3072, 'rsa-4096': 4096 };
const CURVES = {
  'ecdsa-p256': { wire: 'ecdsa-sha2-nistp256', id: 'nistp256', jwk: 'P-256', size: 32 },
  'ecdsa-p384': { wire: 'ecdsa-sha2-nistp384', id: 'nistp384', jwk: 'P-384', size: 48 },
};
const BCRYPT_ROUNDS = 16;

const subtle = () => globalThis.crypto.subtle;
const enc = new TextEncoder();

// --- bytes y formato de red de SSH -------------------------------------------------------------------------------------

const u32 = (n) => Uint8Array.of((n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255);
const concat = (...parts) => {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let o = 0;
  for (const p of parts) { out.set(p, o); o += p.length; }
  return out;
};
const sshString = (data) => { const b = typeof data === 'string' ? enc.encode(data) : data; return concat(u32(b.length), b); };
const stripZeros = (b) => { let i = 0; while (i < b.length - 1 && b[i] === 0) i++; return b.subarray(i); };
const mpint = (b) => { const s = stripZeros(b); return sshString(s[0] & 0x80 ? concat(Uint8Array.of(0), s) : s); };

export const toBase64 = (bytes) => { let s = ''; for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000)); return btoa(s); };
export const fromBase64 = (text) => Uint8Array.from(atob(text), (c) => c.charCodeAt(0));
const fromBase64Url = (text) => fromBase64(text.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(text.length / 4) * 4, '='));

/** Huella como `ssh-keygen -lf`: SHA256: + base64 sin relleno del blob de la clave publica. */
export async function fingerprintOfBlob(blob) {
  const digest = new Uint8Array(await subtle().digest('SHA-256', blob));
  return `SHA256:${toBase64(digest).replace(/=+$/, '')}`;
}

/** Huella de una linea authorized_keys (tipo, base64 y comentario). null si no se puede leer. */
export async function fingerprintOfLine(line) {
  const m = /(?:^|\s)(ssh-ed25519|ssh-rsa|ssh-dss|ecdsa-sha2-nistp(?:256|384|521)|sk-ssh-ed25519@openssh\.com|sk-ecdsa-sha2-nistp256@openssh\.com)\s+([A-Za-z0-9+/]+={0,2})/.exec(String(line || ''));
  if (!m) return null;
  try { return await fingerprintOfBlob(fromBase64(m[2])); } catch { return null; }
}

// --- generacion -------------------------------------------------------------------------------------------------------

let ed25519Support = null;
/** ¿Este navegador genera Ed25519 con WebCrypto? (Chrome 137+, Firefox 129+, Safari 17+). Se prueba una vez. */
export async function supportsEd25519() {
  if (ed25519Support === null) {
    try { await subtle().generateKey({ name: 'Ed25519' }, true, ['sign', 'verify']); ed25519Support = true; } catch { ed25519Support = false; }
  }
  return ed25519Support;
}

/** Los componentes de una clave nueva: { keyType, wire, publicBlob, privateFields(comment) => Uint8Array }. */
async function newKey(keyType) {
  if (keyType === 'ed25519') {
    const pair = await subtle().generateKey({ name: 'Ed25519' }, true, ['sign', 'verify']);
    const jwk = await subtle().exportKey('jwk', pair.privateKey);
    const seed = fromBase64Url(jwk.d); const pub = fromBase64Url(jwk.x);
    return {
      keyType,
      wire: 'ssh-ed25519',
      publicBlob: concat(sshString('ssh-ed25519'), sshString(pub)),
      privateFields: (comment) => concat(sshString('ssh-ed25519'), sshString(pub), sshString(concat(seed, pub)), sshString(comment)),
    };
  }
  if (CURVES[keyType]) {
    const c = CURVES[keyType];
    const pair = await subtle().generateKey({ name: 'ECDSA', namedCurve: c.jwk }, true, ['sign', 'verify']);
    const jwk = await subtle().exportKey('jwk', pair.privateKey);
    const point = concat(Uint8Array.of(4), fromBase64Url(jwk.x), fromBase64Url(jwk.y));
    return {
      keyType,
      wire: c.wire,
      publicBlob: concat(sshString(c.wire), sshString(c.id), sshString(point)),
      privateFields: (comment) => concat(sshString(c.wire), sshString(c.id), sshString(point), mpint(fromBase64Url(jwk.d)), sshString(comment)),
    };
  }
  if (RSA_SIZES[keyType]) {
    const pair = await subtle().generateKey({ name: 'RSASSA-PKCS1-v1_5', modulusLength: RSA_SIZES[keyType], publicExponent: Uint8Array.of(1, 0, 1), hash: 'SHA-256' }, true, ['sign', 'verify']);
    const jwk = await subtle().exportKey('jwk', pair.privateKey);
    const [n, e, d, p, q, qi] = ['n', 'e', 'd', 'p', 'q', 'qi'].map((k) => fromBase64Url(jwk[k]));
    return {
      keyType,
      wire: 'ssh-rsa',
      publicBlob: concat(sshString('ssh-rsa'), mpint(e), mpint(n)),
      privateFields: (comment) => concat(sshString('ssh-rsa'), mpint(n), mpint(e), mpint(d), mpint(qi), mpint(p), mpint(q), sshString(comment)),
    };
  }
  throw new Error('unsupported_key_type');
}

const wrap70 = (s) => s.match(/.{1,70}/g).join('\n');

/**
 * La clave privada en formato openssh-key-v1, en texto PEM. Sin passphrase va sin cifrar; con ella, aes256-ctr con una
 * clave de bcrypt_pbkdf (16 rondas), igual que `ssh-keygen -a 16`.
 */
async function encodeOpenSshPrivate(publicBlob, privateFields, passphrase) {
  const encrypted = !!passphrase;
  const blockSize = encrypted ? 16 : 8;
  const check = globalThis.crypto.getRandomValues(new Uint32Array(1))[0];
  let plain = concat(u32(check), u32(check), privateFields);
  const pad = (blockSize - (plain.length % blockSize)) % blockSize;
  plain = concat(plain, Uint8Array.from({ length: pad }, (_, i) => i + 1));

  let kdfOptions = new Uint8Array(0);
  let body = plain;
  if (encrypted) {
    const salt = globalThis.crypto.getRandomValues(new Uint8Array(16));
    const material = bcryptPbkdf(enc.encode(passphrase), salt, 48, BCRYPT_ROUNDS);
    const key = await subtle().importKey('raw', material.slice(0, 32), 'AES-CTR', false, ['encrypt']);
    body = new Uint8Array(await subtle().encrypt({ name: 'AES-CTR', counter: material.slice(32, 48), length: 128 }, key, plain));
    kdfOptions = concat(sshString(salt), u32(BCRYPT_ROUNDS));
  }
  const file = concat(
    enc.encode('openssh-key-v1\0'),
    sshString(encrypted ? 'aes256-ctr' : 'none'), sshString(encrypted ? 'bcrypt' : 'none'), sshString(kdfOptions),
    u32(1), sshString(publicBlob), sshString(body),
  );
  return `-----BEGIN OPENSSH PRIVATE KEY-----\n${wrap70(toBase64(file))}\n-----END OPENSSH PRIVATE KEY-----\n`;
}

/**
 * Genera una clave nueva. `keyType`: ed25519 (por defecto; si el navegador no la soporta, ecdsa-p256), ecdsa-p256,
 * ecdsa-p384, rsa-3072, rsa-4096. Devuelve { privateKey, publicKey, fingerprint, keyType, comment, fellBack }:
 * `fellBack` es true si se pidio ed25519 y se hizo ecdsa-p256.
 */
export async function generateKeyPair({ keyType = 'ed25519', passphrase = '', comment = '' } = {}) {
  let type = keyType;
  let fellBack = false;
  if (type === 'ed25519' && !(await supportsEd25519())) { type = 'ecdsa-p256'; fellBack = true; }
  const key = await newKey(type);
  const cleanComment = String(comment || '').replace(/[\r\n]+/g, ' ').trim();
  return {
    privateKey: await encodeOpenSshPrivate(key.publicBlob, key.privateFields(cleanComment), passphrase),
    publicKey: `${key.wire} ${toBase64(key.publicBlob)}${cleanComment ? ` ${cleanComment}` : ''}`,
    fingerprint: await fingerprintOfBlob(key.publicBlob),
    keyType: type,
    comment: cleanComment,
    fellBack,
  };
}

// --- lectura ligera ---------------------------------------------------------------------------------------------------

/** ¿Parece una clave privada (en cualquiera de los formatos que acepta el servidor)? */
export const looksPrivate = (text) => /-----BEGIN [A-Z0-9 ]*PRIVATE KEY-----/.test(String(text || '')) || /^PuTTY-User-Key-File-/m.test(String(text || ''));

/** ¿Esta cifrada con passphrase? Solo mira la cabecera; el servidor es quien valida de verdad. null si no se sabe. */
export function privateKeyIsEncrypted(text) {
  const s = String(text || '');
  if (!looksPrivate(s)) return null;
  if (/BEGIN [A-Z0-9 ]*ENCRYPTED[A-Z0-9 ]*PRIVATE KEY|Proc-Type:\s*4,ENCRYPTED/.test(s)) return true;
  if (/^PuTTY-User-Key-File-/m.test(s)) { const m = /^Encryption:\s*(\S+)/m.exec(s); return m ? m[1] !== 'none' : null; }
  const m = /-----BEGIN OPENSSH PRIVATE KEY-----([\s\S]*?)-----END OPENSSH PRIVATE KEY-----/.exec(s);
  if (!m) return false;
  try {
    const head = fromBase64(m[1].replace(/\s+/g, '').slice(0, 96));
    const len = (head[15] << 24 | head[16] << 16 | head[17] << 8 | head[18]) >>> 0; // tras el magic de 15 bytes
    return new TextDecoder().decode(head.subarray(19, 19 + len)) !== 'none';
  } catch { return null; }
}

// --- servidor ---------------------------------------------------------------------------------------------------------

/**
 * Lo que se sabe de una clave pegada o subida (formato, publica, huella, tipo, passphrase): lo calcula el servidor
 * (POST /ssh-keys/inspect, services/sshKeys.js), una sola implementacion para el panel y la app.
 * Devuelve { ok: true, ...info } o { ok: false, detail, field } con `detail` = ssh_public_key_invalid |
 * ssh_public_key_multiple | ssh_private_key_invalid | ssh_passphrase_wrong | ssh_key_mismatch | request_failed.
 */
export async function inspectKey({ privateKey, publicKey, passphrase }) {
  try {
    const res = await api.post('/ssh-keys/inspect', {
      ...(privateKey ? { privateKey } : {}), ...(publicKey ? { publicKey } : {}), ...(passphrase ? { passphrase } : {}),
    });
    return { ok: true, ...res.data };
  } catch (err) {
    const data = err?.response?.data;
    return { ok: false, detail: data?.detail || 'request_failed', field: data?.field, status: err?.response?.status };
  }
}

/**
 * Un item `ssh` con el formato antiguo (solo `sshKey`) al formato nuevo, para editarlo: la privada va a `privateKey`, la
 * publica a `publicKey`, y se derivan publica, huella y tipo con el servidor. No toca el item guardado: eso solo ocurre
 * al guardar. Si el servidor no puede leerla (formato raro), queda en el campo que le toca para que se corrija a mano.
 */
export async function migrateLegacyData(data) {
  const raw = String(data?.sshKey || '').trim();
  const hasNew = ['privateKey', 'publicKey'].some((k) => String(data?.[k] || '').trim());
  if (!raw || hasNew) {
    if (!('sshKey' in (data || {}))) return data;
    const { sshKey, ...rest } = data; // eslint-disable-line no-unused-vars
    return rest;
  }
  const { sshKey, ...rest } = data; // eslint-disable-line no-unused-vars
  const isPrivate = looksPrivate(raw);
  const next = { ...rest, ...(isPrivate ? { privateKey: raw } : { publicKey: raw }) };
  const info = await inspectKey(isPrivate ? { privateKey: raw, passphrase: data.passphrase } : { publicKey: raw });
  if (info.ok) {
    if (info.publicKey) next.publicKey = info.publicKey;
    if (info.fingerprint) { next.fingerprint = info.fingerprint; next.keyType = info.keyType; }
    if (info.comment && !next.comment) next.comment = info.comment;
  }
  return next;
}

// --- ficheros ---------------------------------------------------------------------------------------------------------

/** Nombre de fichero habitual: id_ed25519, id_rsa, id_ecdsa (y .pub). */
export function keyFileName(keyType, pub) {
  const base = /^rsa/.test(keyType || '') ? 'id_rsa' : /^ecdsa/.test(keyType || '') ? 'id_ecdsa' : 'id_ed25519';
  return pub ? `${base}.pub` : base;
}

/**
 * Guarda un texto como fichero en el dispositivo (descarga del navegador). El tipo es octet-stream a proposito: con
 * text/plain Chrome le añade `.txt` a un nombre sin extension y `id_ed25519` llegaria como `id_ed25519.txt`, que ssh no busca.
 */
export function downloadText(fileName, text) {
  const url = URL.createObjectURL(new Blob([text], { type: 'application/octet-stream' }));
  const a = document.createElement('a');
  a.href = url; a.download = fileName; a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
